import React from "react";
import { BlogPostContent } from "@/types/blog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DOMPurify from "dompurify";

interface BlogContentRendererProps {
  content: string | BlogPostContent;
}

/**
 * Component responsible for rendering blog content based on its type
 * Supports both string content (Markdown) and structured BlogPostContent objects (HTML)
 */
const BlogContentRenderer: React.FC<BlogContentRendererProps> = ({ content }) => {
  console.info("BlogContentRenderer - Content type:", typeof content);

  // If content is a string, render it as MARKDOWN
  if (typeof content === "string") {
    if (!content || content.trim() === "") {
      console.warn("Empty string content detected");
      return (
        <div className="text-center py-12">
          <p className="text-joerg-600">Kein Inhalt verfügbar.</p>
        </div>
      );
    }

    // 2. Ersetze dangerouslySetInnerHTML durch den Parser
    return (
      <div className="prose max-w-none relative z-10 dark:prose-invert">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]} // Fügt GFM-Support hinzu (Tabellen, Links etc.)
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  }
  // If content is a BlogPostContent object with sections (dieser Teil war schon OK)
  else if (content && "sections" in content && Array.isArray(content.sections) && content.sections.length > 0) {
    return (
      <div className="blog-content relative z-10">
        {content.sections.map((section) => (
          <section key={section.id} id={section.id} className="mb-12 relative scroll-mt-20">
            {section.title && (
              <h2 className="text-3xl font-bold text-joerg-800 dark:text-joerg-100 mb-6">{section.title}</h2>
            )}
            <div
              className="prose max-w-none dark:prose-invert font-thin"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(section.content, {
                  ALLOWED_TAGS: ['p', 'strong', 'em', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'a', 'br', 'span', 'div', 'blockquote', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'sup', 'sub', 'hr'],
                  ALLOWED_ATTR: ['href', 'class', 'id', 'target', 'rel', 'src', 'alt', 'title', 'aria-label', 'data-speakable'],
                  ADD_ATTR: ['target'],
                  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|\/)/i
                })
              }}
            />
          </section>
        ))}
      </div>
    );
  }

  // Fallback for empty or invalid content
  console.warn("Fallback content rendering used - no valid content format detected");
  return (
    <div className="text-center py-12">
      <p className="text-joerg-600">Der Inhalt dieses Beitrags konnte nicht geladen werden.</p>
    </div>
  );
};
export default BlogContentRenderer;
